import {
  ATTENDANCE_STATUS,
  STATUS_LABELS,
  STATUS_SYMBOLS,
  type AttendanceStatus,
} from '@projektarbeit/shared';
import { cn } from '@/lib/utils';

export type ExtendedStatus = AttendanceStatus | 'auto-logout';

const EXTENDED_LABELS: Record<ExtendedStatus, string> = {
  ...STATUS_LABELS,
  'auto-logout': 'Auto-Logout',
};

const EXTENDED_SYMBOLS: Record<ExtendedStatus, string> = {
  ...STATUS_SYMBOLS,
  'auto-logout': '⏏',
};

const TONE_CLASSES: Record<ExtendedStatus, string> = {
  [ATTENDANCE_STATUS.ANWESEND]: 'bg-status-present/15 text-status-present border-status-present/30',
  [ATTENDANCE_STATUS.VERSPAETET]: 'bg-status-late/15 text-status-late border-status-late/40',
  [ATTENDANCE_STATUS.FEHLT]: 'bg-status-absent/15 text-status-absent border-status-absent/30',
  [ATTENDANCE_STATUS.EXCUSED_PRAXISMODUL]: 'bg-slate-100 text-slate-700 border-slate-300',
  'auto-logout': 'bg-status-auto-logout/15 text-status-auto-logout border-status-auto-logout/30',
};

export interface StatusBadgeProps {
  status: ExtendedStatus;
  className?: string;
}

/**
 * Renders attendance status as symbol + colored label per CLAUDE.md §6:
 * never colour-only — every state pairs an icon and a German text label.
 */
export function StatusBadge({ status, className }: StatusBadgeProps): JSX.Element {
  const label = EXTENDED_LABELS[status];
  const symbol = EXTENDED_SYMBOLS[status];
  return (
    <span
      role="status"
      aria-label={label}
      data-status={status}
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-sm font-medium',
        TONE_CLASSES[status],
        className,
      )}
    >
      <span aria-hidden>{symbol}</span>
      <span>{label}</span>
    </span>
  );
}
